import moment from 'moment-timezone'
import store from '@/store'
import { notification } from 'ant-design-vue'

const changesHistoryDateTimeFormat = 'MM/DD/YYYY hh:mm A'
const changesHistoryRangeDelimiter = ' -> '

const convertChangesHistoryDateTimePart = (value, timeZone) => {
  const parsedDateTime = moment.utc(value.trim(), changesHistoryDateTimeFormat, true)

  return parsedDateTime.isValid() ? parsedDateTime.tz(timeZone).format(changesHistoryDateTimeFormat) : value
}

export default {
  /**
   * Returns a date range based on the current or custom end date.
   *
   * 📌 This function helps you quickly get a date range like:
   *    - "last 3 months"
   *    - "past 6 weeks"
   *    - "last year"
   *
   * @param {number} amount - How much time to subtract (e.g. 3 for "3 months ago")
   * @param {string} unit - Unit of time ('days', 'weeks', 'months', 'years')
   * @param {Object} options - Optional settings
   * @param {string|Date|moment.Moment} [options.endDate] - Custom end date (default: today)
   * @param {string} [options.format='YYYY-MM-DD'] - Format for the output dates
   *
   * @returns {{ start: string, end: string }}
   *   Returns an object with `start` and `end` dates in the specified format.
   */
  getDateRange(amount, unit = 'months', options = {}) {
    const { endDate = moment(), format = 'YYYY-MM-DD' } = options

    const end = moment(endDate)
    const start = moment(end).subtract(amount, unit)

    return {
      start: start.format(format),
      end: end.format(format)
    }
  },
  
  convertDateTimeToCurrentTimeZoneFormattedString(dateTime) {
    if (this.isEmpty(dateTime)) {
      return null
    }

    return this.convertDateTimeToCurrentTimeZone(dateTime).format(store.state.applicationState.dateTimeFormat)
  },

  convertDateTimeToCurrentTimeZoneWithFormat(dateTime, format) {
    if (this.isEmpty(dateTime)) {
      return null
    }

    return this.convertDateTimeToCurrentTimeZone(dateTime).format(format)
  },

  convertChangesHistoryValueToProjectTimeZone(value, timeZone) {
    if (typeof value !== 'string' || this.isEmpty(value) || this.isEmpty(timeZone)) {
      return value
    }

    if (value.includes(changesHistoryRangeDelimiter)) {
      return value
        .split(changesHistoryRangeDelimiter)
        .map(item => convertChangesHistoryDateTimePart(item, timeZone))
        .join(changesHistoryRangeDelimiter)
    }

    return convertChangesHistoryDateTimePart(value, timeZone)
  },

  convertDateTimeToCurrentTimeZone(dateTime) {
    if (this.isEmpty(dateTime)) {
      return null
    }

    const timeZone = store.state.applicationState.timeZone
    if (this.isEmpty(timeZone)) {
      console.error('Time zone is not defined')
      return null
    }

    return moment.utc(dateTime).seconds(0).milliseconds(0).tz(timeZone)
  },

  convertDateTimeToUTC(dateTime) {
    if (this.isEmpty(dateTime)) return null

    return moment(dateTime).utc().toISOString().substring(0, 16)
  },

  combineAssociateName(firstName, lastName) {
    if (!firstName || !lastName) {
      return ''
    }

    const trimmedFirstName = firstName.trim()
    const trimmedLastName = lastName.trim()

    return trimmedFirstName + ' ' + trimmedLastName
  },

  isEmpty(str) {
    return str === null || str === undefined || str === ''
  },

  // str->decimal (db->ui)
  convertDbToMoney(centsStr) {
    if (!centsStr) return 0
    const cents = parseInt(centsStr)
    return cents / 100
  },

  // decimal->str (ui->db)
  convertMoneyToDb(money) {
    if (this.isEmpty(money)) return null
    return money * 100
  },

  countDuration(startMoment, endMoment, timeUnit) {
    const roundedStartMoment = moment(startMoment).clone().startOf('minute')
    const roundedEndMoment = moment(endMoment).clone().startOf('minute')

    return roundedEndMoment.diff(roundedStartMoment, timeUnit)
  },

  formatMinutesToHoursAndMinutesDuration(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = Math.round(totalMinutes % 60)

    let formattedResult = ''

    if (hours !== 0) formattedResult += hours + 'h'
    if (hours && minutes) formattedResult += ' '
    if (minutes !== 0 || totalMinutes === 0) formattedResult += minutes + 'm'

    return formattedResult
  },

  getFormattedDuration(startMoment, endMoment) {
    if (!startMoment || !endMoment) {
      return this.formatMinutesToHoursAndMinutesDuration(0)
    }

    return this.formatMinutesToHoursAndMinutesDuration(Math.abs(this.countDuration(startMoment, endMoment, 'minutes')))
  },

  capitalize(str) {
    if (typeof str === 'string' && str.length > 0) {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    } else {
      return str
    }
  },

  splitAndCapitalize(str) {
    if (this.isEmpty(str)) {
      return ''
    }

    const words = str.split('_')
    const capitalizedWords = words.map(word => this.capitalize(word))

    return capitalizedWords.join(' ')
  },

  titleCase(str) {
    if (this.isEmpty(str)) {
      return ''
    }

    return String(str)
      .toLowerCase()
      .split(' ')
      .map(word => {
        return word.charAt(0).toUpperCase() + word.slice(1)
      })
      .join(' ')
  },

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  },

  getShortcutNumber(value) {
    if (value == null) return ''

    if (typeof value !== 'number') {
      console.error('Value must be a number or null')

      return value
    }
    if (value < 1000) {
      return value % 1 === 0 ? Math.round(value) : value.toFixed(2)
    } else if (value < 10000) {
      return Math.round(value)
    } else if (value < 1_000_000) {
      return `${Math.round(value / 1000)}k`
    } else {
      return `${Math.round(value / 1_000_000)}m`
    }
  },

  downloadFile(url, title = 'Document(s) Ready to Download') {
    if (!url) {
      notification.warning({
        message: 'Warning',
        description: 'Content Not Found'
      })
      return
    }
    if (!document.hidden) {
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', '')
      link.setAttribute('target', '_blank')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      const notificationKey = `download-${Date.now()}`
      notification.success({
        key: notificationKey,
        message: 'Success',
        description: title,
        duration: 0,
        btn: h => {
          return h(
            'a-button',
            {
              props: {
                type: 'primary',
                size: 'small'
              },
              on: {
                click: () => {
                  const link = document.createElement('a')
                  link.href = url
                  link.setAttribute('download', '')
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                  notification.close(notificationKey)
                }
              }
            },
            'Download'
          )
        }
      })
    }
  }
}
